import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import type { BaseCapabilities } from '@sphere/shared/domain'
import { AnnouncementCategory, AnnouncementPriority } from '../../../enums'

/**
 * Response DTO pour une pièce jointe d'annonce
 */
export class AnnouncementAttachmentResponse {
  @ApiProperty({ description: 'ID unique de la pièce jointe' })
  id!: string

  @ApiProperty({ description: 'Nom du fichier' })
  filename!: string

  @ApiProperty({ description: 'URL de téléchargement' })
  url!: string

  @ApiProperty({ description: 'Taille en octets' })
  size!: number

  @ApiProperty({ description: 'Type MIME du fichier' })
  mimeType!: string
}

/**
 * Response DTO pour une annonce
 */
export class AnnouncementResponse {
  @ApiProperty({ description: 'ID unique' })
  id!: string

  @ApiProperty({ description: 'Date de création', type: 'string', format: 'date-time' })
  createdAt!: Date

  @ApiProperty({ description: 'Date de mise à jour', type: 'string', format: 'date-time' })
  updatedAt!: Date

  @ApiProperty({ description: "Titre de l'annonce" })
  title!: string

  @ApiProperty({ description: "Contenu de l'annonce" })
  content!: string

  @ApiProperty({ enum: AnnouncementPriority, description: 'Priorité' })
  priority!: AnnouncementPriority

  @ApiPropertyOptional({ enum: AnnouncementCategory, description: 'Catégorie' })
  category?: AnnouncementCategory

  @ApiProperty({ description: "ID de l'auteur" })
  authorId!: string

  @ApiProperty({ description: "Nom de l'auteur" })
  authorName!: string

  @ApiProperty({ description: "Rôle de l'auteur depuis @sphere/auth" })
  authorRole!: string

  @ApiPropertyOptional({ description: "ID de l'établissement" })
  establishmentId?: string

  @ApiPropertyOptional({ description: 'ID de la classe' })
  classId?: string

  @ApiPropertyOptional({ description: 'ID de la matière' })
  subjectId?: string

  @ApiProperty({ type: [String], description: 'Rôles cibles' })
  targetRoles!: string[]

  @ApiProperty({ type: [String], description: 'IDs utilisateurs cibles' })
  targetUserIds!: string[]

  @ApiPropertyOptional({ description: 'Date de publication', type: 'string', format: 'date-time' })
  publishedAt?: Date

  @ApiPropertyOptional({ description: "Date d'expiration", type: 'string', format: 'date-time' })
  expiresAt?: Date

  @ApiProperty({ description: 'Nombre de vues' })
  viewCount!: number

  @ApiProperty({ type: () => [AnnouncementAttachmentResponse], description: 'Pièces jointes' })
  attachments!: AnnouncementAttachmentResponse[]

  @ApiProperty({ description: "Indique si l'utilisateur courant a lu l'annonce" })
  isRead!: boolean

  @ApiPropertyOptional({ description: "Date de lecture par l'utilisateur courant", type: 'string', format: 'date-time' })
  readAt?: Date

  @ApiPropertyOptional({
    description: 'Actions autorisées pour l’utilisateur courant sur cette annonce',
    example: { canView: true, canEdit: false, canDelete: false },
  })
  capabilities?: BaseCapabilities
}

/**
 * Réponse paginée d'annonces
 */
export class PaginatedAnnouncementsResponse {
  @ApiProperty({ type: () => [AnnouncementResponse], description: 'Liste des annonces' })
  data!: AnnouncementResponse[]

  @ApiProperty({ description: 'Total des résultats' })
  total!: number

  @ApiProperty({ description: 'Page courante' })
  page!: number

  @ApiProperty({ description: 'Limite par page' })
  limit!: number

  @ApiProperty({ description: 'Nombre total de pages' })
  totalPages!: number
}
